import { useAdminListProductEntitlements } from '@/api/auth/admin/admin';
import {
  AdminEntitlementSyncResponseSource,
  type AdminEntitlementSyncResponse,
} from '@/api/auth/missionImpactAuthAPI.schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatEntitlementRule } from '@/lib/entitlements';

interface OrganizationEntitlementsTableProps {
  organizationId: string;
}

/** Derives a friendly source label from the entitlement's provenance fields.
 * The API serializes EntitlementSource numerically; NUMBER_0 is ManualAdminGrant. */
function sourceLabel(entitlement: AdminEntitlementSyncResponse): string {
  if (entitlement.source === AdminEntitlementSyncResponseSource.NUMBER_0) return 'Manual';
  if (entitlement.trialId) return 'Trial';
  if (entitlement.stripeSubscriptionId) return 'Subscription';
  if (entitlement.isDefault) return 'Default';
  return 'System';
}

/** Formats the active window from optional start/end timestamps. */
function formatWindow(start?: string | null, end?: string | null): string {
  const startText = start ? new Date(start).toLocaleDateString() : null;
  const endText = end ? new Date(end).toLocaleDateString() : null;
  if (startText && endText) return `${startText} – ${endText}`;
  if (startText) return `From ${startText}`;
  if (endText) return `Until ${endText}`;
  return 'No expiry';
}

/**
 * Read-only table of the entitlements across an organization's product
 * registrations (all sources), via the admin product-entitlements feed.
 */
export function OrganizationEntitlementsTable({
  organizationId,
}: OrganizationEntitlementsTableProps) {
  const { data, isLoading, error } = useAdminListProductEntitlements(
    { organizationId, pageSize: 100 },
    { query: { enabled: !!organizationId } }
  );

  const page = data?.data;
  const items = page?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Entitlements</CardTitle>
        <CardDescription>
          Entitlements across this organization's product registrations
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-destructive py-2">Failed to load entitlements.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Rule</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(3)].map((_, index) => (
                    <TableRow key={index}>
                      {[...Array(4)].map((__, cell) => (
                        <TableCell key={cell}>
                          <Skeleton className="h-4 w-[100px]" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                      No entitlements found for this organization.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((entitlement) => (
                    <TableRow key={entitlement.id}>
                      <TableCell className="font-medium font-mono">
                        {entitlement.productId}
                      </TableCell>
                      <TableCell>{formatEntitlementRule(entitlement.rule)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={entitlement.enabled ? 'default' : 'outline'} className="w-fit">
                            {entitlement.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatWindow(entitlement.startUtc, entitlement.endUtc)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{sourceLabel(entitlement)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {page?.hasMore && (
              <p className="text-xs text-muted-foreground mt-2">
                Showing the first 100 entitlements.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
